import { useState } from "react";
import { Webhook, Loader2, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { formatDateTimeAU } from "@/lib/formatDateAU";

/**
 * Compact GHL staff-SMS webhook status pill for the Admin Bookings list.
 *
 * • Lazy-loads notification_logs for the booking on first click (avoids N+1
 *   on the bookings list payload).
 * • Renders a small "GHL: sent ✓" / "GHL: failed ✕" / "GHL: skipped" pill,
 *   plus an inline popover with HTTP status + timestamp + error.
 * • Only relevant for PAID bookings — for unpaid bookings the pill stays
 *   in an "idle" state (GHL fires only on the unpaid→paid transition).
 */
export function GhlNotificationStatus({ booking }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState(null);

  const ref = booking?.reference;
  const isPaid = booking?.is_paid || booking?.status === "paid";

  const load = async () => {
    if (logs || loading) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/bookings/${encodeURIComponent(ref)}/notification-logs`);
      setLogs(Array.isArray(data) ? data : []);
    } catch (err) {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) load();
  };

  const latest = (logs || []).find((l) => l.provider === "gohighlevel");
  let pillColor = "bg-slate-100 text-slate-500";
  let Icon = Webhook;
  let label = "GHL";
  if (!isPaid) {
    pillColor = "bg-slate-50 text-slate-400";
    label = "GHL pending";
  } else if (logs && latest) {
    if (latest.success) {
      pillColor = "bg-emerald-100 text-emerald-700";
      Icon = CheckCircle2;
      label = "GHL sent";
    } else if (latest.mode && latest.mode.startsWith("skipped")) {
      pillColor = "bg-slate-100 text-slate-600";
      Icon = AlertTriangle;
      label = "GHL skipped";
    } else {
      pillColor = "bg-rose-100 text-rose-700";
      Icon = XCircle;
      label = "GHL failed";
    }
  } else if (logs && logs.length === 0) {
    pillColor = "bg-slate-50 text-slate-400";
    label = "GHL not fired";
  }

  const fmt = (d) => formatDateTimeAU(d);

  return (
    <div className="relative">
      <button
        type="button"
        data-testid={`booking-ghl-status-${ref}`}
        onClick={toggle}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${pillColor} hover:opacity-80 transition-opacity`}
        title="GoHighLevel staff-SMS alert status"
      >
        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
        <span>{label}</span>
      </button>
      {open && (
        <div
          data-testid={`booking-ghl-popover-${ref}`}
          className="absolute z-20 mt-1 left-0 w-72 rounded-xl border border-slate-200 bg-white shadow-lg p-3 text-[11px] text-slate-700"
          onMouseLeave={() => setOpen(false)}
        >
          <div className="font-semibold text-slate-900 mb-1.5 flex items-center gap-1">
            <Webhook className="w-3.5 h-3.5 text-cyan-600" /> GHL staff-SMS alert
          </div>
          {loading && <div className="italic text-slate-500">Loading…</div>}
          {!loading && (!logs || logs.length === 0) && (
            <div className="italic text-slate-500">
              No webhook fired yet for this booking.{" "}
              {!isPaid && "GHL fires only when the booking becomes paid."}
            </div>
          )}
          {!loading && latest && (
            <div className="space-y-0.5">
              <div>
                <span className="font-medium">Result:</span>{" "}
                {latest.success ? (
                  <span className="text-emerald-700 font-semibold">Delivered to GHL</span>
                ) : latest.mode && latest.mode.startsWith("skipped") ? (
                  <span className="text-slate-700 font-semibold">Skipped ({latest.mode.replace("skipped_", "")})</span>
                ) : (
                  <span className="text-rose-700 font-semibold">Failed</span>
                )}
              </div>
              {latest.response_status != null && (
                <div><span className="font-medium">HTTP:</span> <span className="font-mono">{latest.response_status}</span></div>
              )}
              <div><span className="font-medium">Mode:</span> <span className="font-mono">{latest.mode}</span></div>
              <div><span className="font-medium">At:</span> <span className="font-mono">{fmt(latest.created_at)}</span></div>
              {latest.error_message && (
                <div className="text-rose-700 break-all"><span className="font-medium">Error:</span> {latest.error_message}</div>
              )}
              {logs.length > 1 && (
                <div className="mt-1 text-slate-500 italic">{logs.length} total attempts logged.</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
